import React, { useEffect, useState } from 'react'

import { observer } from 'mobx-react-lite'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'

import { useStores } from '@trivie/core'
import { Button } from '@trivie/ui-web/components/button'

import { CircularProgress, TextField } from '@mui/material'
import Placeholder from '@tiptap/extension-placeholder'
import { Resource } from '@trivie/core/src/models/Resource'
import { ResourceApi } from '@trivie/core/src/services/api-objects'

interface TextResourceTabProps {
  quiz: any
  standalone?: boolean
  topic?: any
  onUpdate?: any
  onSelect?: any
  selected?: any
}
export const TextResourceTab = observer((props: TextResourceTabProps) => {
  const { onSelect, quiz, standalone, topic } = props
  const { resourceStore } = useStores()
  const {
    setTextContent,
    textContent,
    setTextTitle,
    textTitle,
    uploadTextResource,
    getSourceDetails,
  } = resourceStore

  const textEditor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({
        emptyEditorClass: 'is-editor-empty',
        placeholder: 'Paste in text to create a new source.',
      }),
    ],
    onUpdate: ({ editor, transaction }) => {
      setTextContent(editor.getJSON())
    },
    content: textContent ? textContent : '',
    editorProps: {
      attributes: { class: standalone ? 'StandaloneText DocViewer' : 'EditTextResource DocViewer' },
    },
  })

  useEffect(() => {
    if (textEditor) {
      setTimeout(() => {
        textEditor?.commands.setContent(textContent)
      }, 0)
    }
  }, [textContent])

  const [loading, setLoading] = useState(false)
  const submitTextResource = async () => {
    const text = textEditor?.getText() as string
    setLoading(true)
    const { source_id } = (await uploadTextResource(text, textTitle).then((id) => {
      if (!standalone) {
        setLoading(false)
      }
      setTextContent({})
      setTextTitle('')
      return id
    })) as any

    if (standalone && source_id) {
      ResourceApi.getSourceDetails(source_id).then((details) => {
        setLoading(false)
        if (details) {
          const resource = Resource.create({
            ...details.data,
            is_required: false,
            identityBreaker: Math.random() * 999999999,
          })
          if (details?.data.converted_pdf_url || details?.data.filename_or_url) {
            resource.set(
              'preview_url',
              details?.data.converted_pdf_url || details?.data.filename_or_url,
            )
          }
          if (topic) {
            topic.addResource(resource)
          } else if (quiz) {
            quiz.addResource(resource)
          }
          onSelect(resource)
        }
      })
    } else if (source_id) {
      ResourceApi.getSourceDetails(source_id).then((details) => {
        if (details) {
          setLoading(false)
          const resource = Resource.create({
            ...details.data,
            is_required: false,
            identityBreaker: Math.random() * 999999999,
          })
          if (details?.data.converted_pdf_url || details?.data.filename_or_url) {
            resource.set(
              'preview_url',
              details?.data.converted_pdf_url || details?.data.filename_or_url,
            )
          }
          quiz.setMercuryResource(resource)
          quiz.addResource(resource)
        }
      })
    }
  }

  const [touched, setTouched] = useState(false)

  ///128 is max allowed length of Name field
  const cropText = (text) => {
    setTouched(true)
    if (text?.length > 128) {
      setTextTitle(text.slice(0, 128))
    } else {
      setTextTitle(text)
    }
  }

  return quiz?.mercuryResource && !standalone ? null : (
    <div style={{ flexDirection: 'column', display: 'flex' }}>
      <TextField
        size="small"
        variant="outlined"
        placeholder="Name"
        onChange={(e) => cropText(e.target.value)}
        style={{ marginBottom: 12 }}
        inputProps={{ style: { backgroundColor: '#fff' }, borderRadius: 8 }}
        value={textTitle}
        error={touched && (!textTitle || !textTitle?.trim().length)}
        helperText={
          touched && (!textTitle || !textTitle?.trim().length) ? 'Title cannot be blank' : ''
        }
      />
      <EditorContent editor={textEditor} />
      {loading ? (
        <CircularProgress
          style={{ alignSelf: standalone ? 'flex-end' : 'center', marginTop: 12 }}
        />
      ) : standalone ? (
        <Button
          sx={{ marginTop: '16px !important', alignSelf: 'flex-end' }}
          disabled={!textEditor?.getText() || !textTitle || !textTitle?.trim().length}
          onClick={submitTextResource}
          text="Add Source"
        />
      ) : (
        <Button
          sx={{
            width: '185px',
            margin: '0 auto !important',
            display: 'flex',
            marginTop: '16px !important',
          }}
          disabled={!textEditor?.getText() || !textTitle}
          onClick={submitTextResource}
          size="small"
          text="Next"
        />
      )}
    </div>
  )
})
